import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'

import { useAdsByCampaign } from '../../demo/selectors'
import { demoProducts, useDemoStore } from '../../demo/store'
import type { Campaign, CreativeLibraryItem } from '../../demo/types'
import { Button, Icon, useScrollLock, VideoLightbox, VideoLightboxField, VideoLightboxFooter } from '../../ui'
import styles from './CreativeLightbox.module.css'

interface CreativeLightboxProps {
  creative: CreativeLibraryItem | null
  onClose: () => void
  onUse: (creative: CreativeLibraryItem) => void
  onToast: (message: string) => void
}

function assetUrl(path: string) {
  return !path || path.startsWith('/') || path.startsWith('data:') ? path : `/${path}`
}

function timeLabel(seconds: number) {
  if (!Number.isFinite(seconds)) return '0:00'
  return `${Math.floor(seconds / 60)}:${String(Math.floor(seconds % 60)).padStart(2, '0')}`
}

function CampaignUsageLinks({ campaign, creativeId }: { campaign: Campaign; creativeId: string }) {
  const ads = useAdsByCampaign(campaign.id).filter((ad) => ad.creativeId === creativeId)
  return <>{ads.map((ad) => <Link key={ad.id} to={`/monitor/${ad.campaignId}?focus=creatives`}>{ad.name}</Link>)}</>
}

export function CreativeLightbox({ creative, onClose, onUse, onToast }: CreativeLightboxProps) {
  const shellRef = useRef<HTMLDivElement>(null)
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(creative?.name ?? '')
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const renameCreative = useDemoStore((state) => state.renameCreative)
  const deleteCreative = useDemoStore((state) => state.deleteCreative)
  const campaigns = useDemoStore((state) => state.campaigns)
  useScrollLock(creative !== null)

  useEffect(() => {
    if (!creative) return
    const close = (event: KeyboardEvent) => { if (event.key === 'Escape' && !editing) onClose() }
    window.addEventListener('keydown', close)
    return () => window.removeEventListener('keydown', close)
  }, [creative, editing, onClose])
  useEffect(() => {
    if (!creative || creative.kind !== 'video') return
    const video = shellRef.current?.querySelector('video')
    if (!video) return
    const sync = () => { setCurrentTime(video.currentTime); setDuration(video.duration) }
    video.addEventListener('timeupdate', sync)
    video.addEventListener('loadedmetadata', sync)
    void video.play().catch(() => undefined)
    return () => {
      video.removeEventListener('timeupdate', sync)
      video.removeEventListener('loadedmetadata', sync)
    }
  }, [creative])

  if (!creative) return null
  const product = demoProducts.find((item) => item.id === creative.productId)
  const productCampaigns = campaigns.filter((campaign) => campaign.productId === creative.productId)
  const saveName = () => {
    const name = draft.trim()
    if (name && name !== creative.name) renameCreative(creative.id, name)
    else setDraft(creative.name)
    setEditing(false)
  }
  const source = creative.source === 'ai' ? 'AI 生成' : creative.source === 'upload' ? '我的上传' : (
    // 授权日期整体不断行：默认在连字符后可断，会把 2027-03-31 劈成两行
    <>达人授权{creative.kol ? ` · @${creative.kol.replace(/^@/, '')}` : ''}{creative.licenseUntil ? <> · <span className={styles.nowrapValue}>授权至 {creative.licenseUntil}</span></> : null}</>
  )
  const licenseDays = creative.licenseUntil ? Math.ceil((new Date(creative.licenseUntil).getTime() - new Date('2026-09-03').getTime()) / 86400000) : null
  const fields = (
    <>
      <VideoLightboxField label="名称">{editing ? <input autoFocus className={styles.nameInput} value={draft} onChange={(event) => setDraft(event.target.value)} onBlur={saveName} onKeyDown={(event) => {
        if (event.key === 'Enter') event.currentTarget.blur()
        if (event.key === 'Escape') { event.stopPropagation(); setDraft(creative.name); setEditing(false) }
      }} /> : <span className={styles.nameValue}>{creative.name}<button type="button" aria-label="重命名" onClick={() => setEditing(true)}><Icon name="pencil" size={14} /></button></span>}</VideoLightboxField>
      <VideoLightboxField label="来源"><span>{source}</span>{licenseDays !== null && licenseDays >= 0 && licenseDays < 30 ? <span className={styles.expiring}>即将到期</span> : null}</VideoLightboxField>
      <VideoLightboxField label="关联商品">{productCampaigns[0] ? <Link to={`/monitor/${productCampaigns[0].id}`}>{product?.name ?? creative.productId}</Link> : product?.name ?? creative.productId}</VideoLightboxField>
      <VideoLightboxField label="规格">{creative.kind === 'video' ? `视频 ${creative.duration ?? '0:15'} · 1080×1350` : '图片 1080×1350'}</VideoLightboxField>
      <VideoLightboxField label="使用情况">{creative.usedByAds > 0 ? <span>用于 {creative.usedByAds} 条广告：<span className={styles.usageLinks}>{productCampaigns.map((campaign) => <CampaignUsageLinks key={campaign.id} campaign={campaign} creativeId={creative.id} />)}</span></span> : '还没有广告在用'}</VideoLightboxField>
      <VideoLightboxField label="合规状态">{creative.compliance ? <span className={creative.compliance.level === 'violation' ? styles.violation : styles.risk}>{creative.compliance.level === 'violation' ? '违规' : '有风险'} · {creative.compliance.reason}</span> : <span className={styles.normal}>正常</span>}</VideoLightboxField>
      <VideoLightboxFooter><Button className={styles.useButton} onClick={() => onUse(creative)}>用于广告</Button><span className={styles.deleteWrap} title={creative.source === 'kol' ? '授权素材由平台方提供，不能删除' : undefined}><Button variant="ghost" disabled={creative.source === 'kol'} onClick={() => {
        const result = deleteCreative(creative.id)
        if (result.ok) { onClose(); onToast('已删除') }
        else onToast(creative.usedByAds > 0 ? `这条素材正在 ${creative.usedByAds} 条广告里投放，先在广告里换掉再删` : result.reason ?? '暂时不能删除')
      }}>删除</Button></span></VideoLightboxFooter>
    </>
  )

  return (
    <div className={styles.scrim} role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose() }}>
      <div className={styles.shell} ref={shellRef} data-scope="creative-lightbox">
        <button className={styles.close} type="button" aria-label="关闭" onClick={onClose}><Icon name="x" size={20} /></button>
        {creative.kind === 'video' ? <VideoLightbox key={creative.id} src={creative.dataUrl || creative.clip || ''} poster={creative.dataUrl ? undefined : creative.cover} durationLabel={creative.duration ?? timeLabel(duration)} currentLabel={timeLabel(currentTime)} progress={duration ? currentTime / duration : 0}>{fields}</VideoLightbox> : (
          <section className={styles.lightbox}><div className={styles.figure}><img src={assetUrl(creative.dataUrl || creative.cover)} alt={creative.name} /></div><aside className={styles.side}>{fields}</aside></section>
        )}
      </div>
    </div>
  )
}
